import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Col, Dropdown, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ReportCardView from "./ReportCardView";
import { BLOCK_INFO, DEFAULT_LAYOUT, SAMPLE_REPORT, STUDENT_FIELDS } from "./layoutDefaults";
import { schoolFromToken } from "./schoolInfo";

const clone = (o) => JSON.parse(JSON.stringify(o));

/** read an uploaded image and shrink it (max 1600px wide, <= ~450 KB) -> data URL stored in the layout */
const MAX_IMAGE_CHARS = 600000;
const compressHeaderImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("This file is not a valid image"));
      img.onload = () => {
        const scale = Math.min(1, 1600 / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff"; // transparent PNG -> white paper
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        let q = 0.9;
        let out = canvas.toDataURL("image/jpeg", q);
        while (out.length > MAX_IMAGE_CHARS && q > 0.4) {
          q -= 0.1;
          out = canvas.toDataURL("image/jpeg", q);
        }
        if (out.length > MAX_IMAGE_CHARS) reject(new Error("Image is too large, please use a smaller image"));
        else resolve(out);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
const newId = (type) => `${type.toLowerCase()}_${Date.now().toString(36)}`;

const SOURCE_LABEL = {
  CLASS: "this class's own layout",
  SCHOOL_DEFAULT: "the School Default layout",
  BUILT_IN: "the built-in standard layout",
};

/**
 * Settings -> Card Layout (UI designer)
 * - School Default layout (all classes) and optional class-wise layouts
 * - Reorder blocks (drag or arrows), show/hide, rename headings, custom text / space / signature blocks
 * - Live preview with sample data
 */
const CardLayoutTab = ({ canEdit }) => {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("default");
  const [info, setInfo] = useState(null); // response from GET /layout
  const [layout, setLayout] = useState(clone(DEFAULT_LAYOUT));
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const school = useMemo(schoolFromToken, []);

  useEffect(() => {
    reportCardApi.activeClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const load = async (cid = classId) => {
    try {
      const res = await reportCardApi.layout.get(cid);
      setInfo(res);
      // edit the stored layout if this level has one, otherwise start from what is used today
      setLayout(clone(res.stored || res.effective || DEFAULT_LAYOUT));
      setSelectedId(null);
      setDirty(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(classId); }, [classId]);

  const changeClass = (cid) => {
    if (dirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    setClassId(cid);
  };

  /* ---------- editing helpers ---------- */
  const update = (fn) => {
    setLayout((l) => {
      const next = clone(l);
      fn(next);
      return next;
    });
    setDirty(true);
  };
  const setSetting = (k, v) => update((l) => { l.settings[k] = v; });
  const setBlock = (id, patch) => update((l) => { Object.assign(l.blocks.find((b) => b.id === id), patch); });
  const move = (from, to) => {
    if (to < 0 || to >= layout.blocks.length || from === to) return;
    update((l) => { const [b] = l.blocks.splice(from, 1); l.blocks.splice(to, 0, b); });
  };
  const removeBlock = (id) => update((l) => { l.blocks = l.blocks.filter((b) => b.id !== id); });
  const addBlock = (type) => {
    const block = { id: newId(type), type, visible: true };
    if (type === "TEXT") Object.assign(block, { text: "Write your note here", align: "left", bold: false });
    if (type === "SPACER") block.height = 16;
    if (type === "SIGNATURES") block.labels = ["Parent's Signature"];
    const at = selectedId ? layout.blocks.findIndex((b) => b.id === selectedId) + 1 : layout.blocks.length;
    update((l) => { l.blocks.splice(at, 0, block); });
    setSelectedId(block.id);
  };

  /* ---------- save / reset ---------- */
  const save = async () => {
    try {
      const res = await reportCardApi.layout.save(classId, layout);
      toast.success(res.message || "Layout saved");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const reset = async () => {
    const msg = classId === "default"
      ? "Delete the School Default layout? Classes without their own layout will use the built-in standard layout."
      : "Remove this class's own layout? The class will use the School Default layout.";
    if (!window.confirm(msg)) return;
    try {
      await reportCardApi.layout.reset(classId);
      toast.success("Layout reset");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const startFromBuiltIn = () => {
    if (!window.confirm("Replace the editor content with the built-in standard layout? (Not saved until you click Save.)")) return;
    setLayout(clone(info?.built_in || DEFAULT_LAYOUT));
    setDirty(true);
  };

  const hasOwn = !!info?.stored;
  const classHasLayout = (id) => (info?.classes_with_layout || []).includes(id);
  const levelName = classId === "default" ? "School Default" : classes.find((c) => c.id === classId)?.class_name || "this class";

  /* ---------- block option editors ---------- */
  // plain function (not a component) so inputs keep focus while typing
  const blockOptions = (b) => {
    const titleInput = (placeholder) => (
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Heading</Form.Label>
        <Form.Control size="sm" value={b.title || ""} placeholder={placeholder} onChange={(e) => setBlock(b.id, { title: e.target.value })} />
      </Form.Group>
    );
    const check = (key, label) => (
      <Form.Check id={`${b.id}-${key}`} className="small" label={label} checked={b[key] !== false}
        onChange={(e) => setBlock(b.id, { [key]: e.target.checked })} />
    );

    switch (b.type) {
      case "HEADER": {
        const mode = b.mode === "IMAGE" ? "IMAGE" : "TEXT";
        const onFile = async (e) => {
          const file = e.target.files && e.target.files[0];
          e.target.value = "";
          if (!file) return;
          if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return toast.error("Please choose a PNG, JPG or WEBP image");
          if (file.size > 10 * 1024 * 1024) return toast.error("Image must be smaller than 10 MB");
          try {
            const dataUrl = await compressHeaderImage(file);
            setBlock(b.id, { mode: "IMAGE", image: dataUrl });
          } catch (err) {
            toast.error(err.message);
          }
        };
        return (
          <>
            <Form.Label className="small mb-1">Header type</Form.Label>
            <div className="d-flex gap-3 mb-2">
              <Form.Check type="radio" id={`${b.id}-m-text`} name={`${b.id}-mode`} className="small" label="School details (text)"
                checked={mode === "TEXT"} onChange={() => setBlock(b.id, { mode: "TEXT" })} />
              <Form.Check type="radio" id={`${b.id}-m-img`} name={`${b.id}-mode`} className="small" label="Image (letterhead)"
                checked={mode === "IMAGE"} onChange={() => setBlock(b.id, { mode: "IMAGE" })} />
            </div>
            {mode === "TEXT" ? (
              <small className="text-muted">School name, address and logo come from school profile. Title and logo options are in Card Settings above.</small>
            ) : (
              <>
                <Form.Control size="sm" type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="mb-2" />
                {b.image ? (
                  <div className="border rounded p-1 mb-2 text-center bg-white">
                    <img src={b.image} alt="Header" style={{ maxWidth: "100%", maxHeight: 90 }} />
                  </div>
                ) : (
                  <small className="text-danger d-block mb-2">No image yet – upload your school letterhead (wide image, e.g. 1600 × 250 px). Until then the text header is printed.</small>
                )}
                <Form.Label className="small mb-0">Image height: {b.image_height || 120}px</Form.Label>
                <Form.Range min={40} max={300} value={b.image_height || 120}
                  onChange={(e) => setBlock(b.id, { image_height: Number(e.target.value) })} />
                <Form.Check id={`${b.id}-st`} className="small" label="Show card title & session below image"
                  checked={b.show_title !== false} onChange={(e) => setBlock(b.id, { show_title: e.target.checked })} />
                {b.image && (
                  <Button size="sm" variant="outline-danger" className="mt-2"
                    onClick={() => setBlock(b.id, { image: "", mode: "TEXT" })}>
                    <i className="fa-solid fa-trash me-1" />Remove image
                  </Button>
                )}
              </>
            )}
          </>
        );
      }
      case "STUDENT_INFO":
        return (
          <>
            {titleInput("(no heading)")}
            <Form.Label className="small mb-0">Show fields</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {STUDENT_FIELDS.map((f) => {
                const on = (b.fields || []).includes(f.key);
                return (
                  <Form.Check key={f.key} id={`${b.id}-${f.key}`} className="small" label={f.label} checked={on}
                    onChange={() => setBlock(b.id, {
                      fields: on ? b.fields.filter((x) => x !== f.key)
                        : STUDENT_FIELDS.map((x) => x.key).filter((k) => k === f.key || (b.fields || []).includes(k)),
                    })} />
                );
              })}
            </div>
          </>
        );
      case "SCHOLASTIC":
        return (
          <>
            {titleInput("Scholastic Area")}
            {check("show_theory_practical", "Show exam type columns (Theory / Practical / Notebook / Project …)")}
            {check("show_grand_total", "Show Grand Total column")}
            {check("show_percentage", "Show Percentage")}
            {check("show_result", "Show Result (Pass / Fail)")}
          </>
        );
      case "CO_SCHOLASTIC":
        return titleInput("Co-Scholastic Area");
      case "FOOTER":
        return (
          <>
            {titleInput("(no heading)")}
            <small className="text-muted">Which rows appear here is set in the Report Footer tab.</small>
          </>
        );
      case "SIGNATURES":
        return (
          <>
            <Form.Label className="small mb-0">Labels (left to right). A label "Date" prints the issue date.</Form.Label>
            {(b.labels || []).map((l, i) => (
              <div key={i} className="d-flex gap-1 mb-1">
                <Form.Control size="sm" value={l} onChange={(e) => setBlock(b.id, { labels: b.labels.map((x, j) => (j === i ? e.target.value : x)) })} />
                <Button size="sm" variant="outline-danger" onClick={() => setBlock(b.id, { labels: b.labels.filter((_, j) => j !== i) })}>
                  <i className="fa fa-xmark"></i>
                </Button>
              </div>
            ))}
            {(b.labels || []).length < 5 && (
              <Button size="sm" variant="link" className="p-0" onClick={() => setBlock(b.id, { labels: [...(b.labels || []), "Signature"] })}>+ Add label</Button>
            )}
          </>
        );
      case "GRADE_LEGEND":
        return (
          <>
            {titleInput("(no instruction line)")}
            {check("show_scholastic", "Show scholastic grade table")}
            {check("show_co_scholastic", "Show co-scholastic grade table")}
          </>
        );
      case "TEXT":
        return (
          <>
            <Form.Control as="textarea" rows={3} size="sm" className="mb-2" value={b.text || ""} onChange={(e) => setBlock(b.id, { text: e.target.value })} />
            <div className="d-flex align-items-center gap-3">
              <ButtonGroup size="sm">
                {["left", "center", "right"].map((a) => (
                  <Button key={a} variant={b.align === a ? "secondary" : "outline-secondary"} onClick={() => setBlock(b.id, { align: a })}>
                    <i className={`fa fa-align-${a}`}></i>
                  </Button>
                ))}
              </ButtonGroup>
              <Form.Check id={`${b.id}-bold`} className="small" label="Bold" checked={!!b.bold} onChange={(e) => setBlock(b.id, { bold: e.target.checked })} />
            </div>
          </>
        );
      case "SPACER":
        return (
          <Form.Group>
            <Form.Label className="small mb-0">Height: {b.height || 10}px</Form.Label>
            <Form.Range min={2} max={100} value={b.height || 10} onChange={(e) => setBlock(b.id, { height: Number(e.target.value) })} />
          </Form.Group>
        );
      default:
        return null;
    }
  };

  const blockLabel = (b) => {
    const base = BLOCK_INFO[b.type]?.label || b.type;
    if (b.type === "TEXT" && b.text) return `${base}: “${b.text.slice(0, 30)}${b.text.length > 30 ? "…" : ""}”`;
    if (b.type === "SIGNATURES") return `${base}: ${(b.labels || []).join(" · ")}`;
    return base;
  };

  return (
    <>
      <Row className="g-2 mb-2 align-items-end">
        <Col md={4}>
          <Form.Label className="form-view-label">Layout for</Form.Label>
          <Form.Select value={classId} onChange={(e) => changeClass(e.target.value)}>
            <option value="default">School Default (all classes)</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.class_name || c.classname}{classHasLayout(c.id) ? "  ★ own layout" : ""}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={8} className="text-md-end">
          {canEdit && (
            <div className="d-inline-flex flex-wrap gap-2">
              <Button size="sm" variant="outline-secondary" onClick={startFromBuiltIn}>Start from standard</Button>
              {hasOwn && <Button size="sm" variant="outline-danger" onClick={reset}>{classId === "default" ? "Delete School Default" : "Remove class layout"}</Button>}
              <Button size="sm" onClick={save} disabled={!dirty && hasOwn}>
                {hasOwn ? "Save" : `Save as ${levelName} layout`}
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {info && (
        <Alert variant={hasOwn ? "success" : "info"} className="py-2 small">
          {hasOwn
            ? <>You are editing the <b>{levelName}</b> layout.{classId === "default" ? " Every class without its own layout uses it." : ""}</>
            : <>{levelName} has no layout of its own and currently uses <b>{SOURCE_LABEL[info.effective_source]}</b>.
              Change anything below and save to give {classId === "default" ? "the school" : "this class"} its own layout.</>}
          {" "}Published cards keep the layout they were published with.
        </Alert>
      )}

      <Row className="g-3">
        {/* ---------------- editor ---------------- */}
        <Col lg={5}>
          <div className="border rounded p-2 mb-3">
            <b className="small">Card Settings</b>
            <Row className="g-2 mt-1">
              <Col xs={12}>
                <Form.Control size="sm" placeholder="Card title (empty = automatic, e.g. Annual Report Card)" value={layout.settings?.title || ""}
                  disabled={!canEdit} onChange={(e) => setSetting("title", e.target.value)} />
              </Col>
              <Col xs={6}>
                <Form.Label className="small mb-0">Font size: {layout.settings?.font_size || 11}px</Form.Label>
                <Form.Range min={8} max={16} value={layout.settings?.font_size || 11} disabled={!canEdit}
                  onChange={(e) => setSetting("font_size", Number(e.target.value))} />
              </Col>
              <Col xs={6}>
                <Form.Check id="ls-logo" className="small" label="Show logo" checked={layout.settings?.show_logo !== false}
                  disabled={!canEdit} onChange={(e) => setSetting("show_logo", e.target.checked)} />
                <Form.Check id="ls-addr" className="small" label="Show school address" checked={layout.settings?.show_school_address !== false}
                  disabled={!canEdit} onChange={(e) => setSetting("show_school_address", e.target.checked)} />
              </Col>
            </Row>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-1">
            <b className="small">Blocks (top to bottom) – drag or use arrows</b>
            {canEdit && (
              <Dropdown>
                <Dropdown.Toggle size="sm" variant="outline-primary">+ Add Block</Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => addBlock("TEXT")}><i className="fa fa-font me-2"></i>Custom Text / Note</Dropdown.Item>
                  <Dropdown.Item onClick={() => addBlock("SPACER")}><i className="fa fa-arrows-up-down me-2"></i>Blank Space</Dropdown.Item>
                  <Dropdown.Item onClick={() => addBlock("SIGNATURES")}><i className="fa fa-signature me-2"></i>Signature Line</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          <div>
            {layout.blocks.map((b, i) => {
              const core = BLOCK_INFO[b.type]?.core;
              const open = selectedId === b.id;
              return (
                <div key={b.id}
                  className={`border rounded mb-1 ${open ? "border-primary" : ""} ${b.visible === false ? "bg-light" : "bg-white"}`}
                  draggable={canEdit}
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragIndex !== null) move(dragIndex, i); setDragIndex(null); }}>
                  <div className="d-flex align-items-center gap-1 p-1" style={{ cursor: canEdit ? "grab" : "default" }}>
                    <i className="fa fa-grip-vertical text-muted px-1"></i>
                    {canEdit && (
                      <>
                        <Button size="sm" variant="light" className="px-1 py-0" disabled={i === 0} onClick={() => move(i, i - 1)}><i className="fa fa-arrow-up"></i></Button>
                        <Button size="sm" variant="light" className="px-1 py-0" disabled={i === layout.blocks.length - 1} onClick={() => move(i, i + 1)}><i className="fa fa-arrow-down"></i></Button>
                      </>
                    )}
                    <span className={`flex-grow-1 small ms-1 ${b.visible === false ? "text-muted text-decoration-line-through" : ""}`}
                      style={{ cursor: "pointer" }} onClick={() => setSelectedId(open ? null : b.id)}>
                      <i className={`fa ${BLOCK_INFO[b.type]?.icon || "fa-square"} me-1`}></i>
                      {blockLabel(b)}
                      {b.title && b.type !== "TEXT" && <span className="text-muted"> – {b.title.slice(0, 25)}</span>}
                    </span>
                    {canEdit && (
                      <>
                        <Form.Check type="switch" id={`vis-${b.id}`} title="Show on card" checked={b.visible !== false}
                          onChange={(e) => setBlock(b.id, { visible: e.target.checked })} />
                        {!core && (
                          <Button size="sm" variant="outline-danger" className="px-1 py-0" onClick={() => removeBlock(b.id)} title="Delete block">
                            <i className="fa fa-trash"></i>
                          </Button>
                        )}
                      </>
                    )}
                    <Button size="sm" variant="link" className="px-1 py-0" onClick={() => setSelectedId(open ? null : b.id)}>
                      <i className={`fa fa-chevron-${open ? "up" : "down"}`}></i>
                    </Button>
                  </div>
                  {open && canEdit && (
                    <div className="border-top p-2">
                      {blockOptions(b)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <small className="text-muted d-block mt-2">
            <Badge bg="light" text="dark">Tip</Badge> Core blocks can be hidden but not deleted. Rows inside the tables (subjects, co-scholastic subjects, footer fields) are ordered in their own tabs.
          </small>
        </Col>

        {/* ---------------- live preview ---------------- */}
        <Col lg={7}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <b className="small">Live preview (sample data)</b>
            {dirty && <Badge bg="warning" text="dark">Unsaved changes</Badge>}
          </div>
          <div style={{ background: "#eee", padding: 8, maxHeight: "75vh", overflowY: "auto" }}>
            <ReportCardView report={SAMPLE_REPORT} layout={layout} school={school} highlightId={selectedId} />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default CardLayoutTab;
